/**
 * Response shapes for MCP's admin API — all 17 endpoints.
 *
 * Taken from the LIVE dev API and cross-checked against
 * docs/products/mcp-api-reference.md, which documents what each field means
 * and which of them mislead.
 *
 * NOTE how `business_id` is handled: it is SEARCHABLE BUT NEVER RENDERED.
 *
 * The API sends it on several responses. Ops know merchants by
 * `business_reference`, and the reference doc is explicit that a console must
 * never surface the UUID — a UUID beside a reference number invites copying
 * the wrong one into a ticket, and nobody can read one aloud.
 *
 * It was previously absent from these types entirely, so that using it was a
 * compile error. That guard cost us the one case it should have allowed: the
 * entitlements table keys on this id, so a UUID pasted out of a DynamoDB
 * console matched nothing at all — the place an operator is most likely to be
 * copying from was the place the console could not help (S0.1 / DA-3335).
 *
 * So the field now exists and is typed `SearchOnly`, which is a string that
 * cannot be assigned to a `string` parameter or rendered as a ReactNode
 * without an explicit cast. Matching on it costs nothing; displaying it still
 * has to be a deliberate act a reviewer can see.
 */

/**
 * A value the console may MATCH on but must not DISPLAY.
 *
 * The brand makes rendering a type error rather than a review question —
 * `{tenant.business_id}` does not compile, while
 * `searchText.includes(business_id)` does, because `searchTextOf` unwraps it
 * once, in one place, on purpose.
 */
export type SearchOnly = string & { readonly __searchOnly: unique symbol };

/** Unwrap a search-only value at the single site allowed to read it. */
export function searchOnlyValue(v: SearchOnly | undefined | null): string {
  return typeof v === "string" ? v : "";
}

import type { Tone } from "../components/tone";

// ---------------------------------------------------------------- tenants --

export interface McpTenantSummary {
  /** What ops know a merchant by. */
  business_reference: string;
  business_name: string;
  email: string;
  /**
   * The entitlements table's key. Searchable, never rendered — see the module
   * docstring. Optional because not every response carries it.
   */
  business_id?: SearchOnly;
  /**
   * The STORED tier, or null when the merchant has no entitlements row.
   *
   * Null is not "free". The server treats a merchant with no row AS free when
   * resolving caps, but nothing has been stored — a read never creates the
   * row. Rendering null as "free" would tell an operator a decision had been
   * made that never was, and hide the difference between a deliberate free
   * tier and a merchant nobody has touched.
   */
  tier: string | null;
  /** Merchant-wide block — the ("<ref>", "*") row. NOT set by a per-user block. */
  blocked: boolean;
  /**
   * Emails blocked at THIS merchant specifically — being REMOVED from this
   * response, and already absent in dev.
   *
   * MCP is moving the names off the fleet list and onto tenant detail, leaving
   * a count here. Both fields are optional because the two environments are
   * mid-rollout: dev sends the count, prod still sends the array, and a console
   * that assumed either one crashes against the other. It crashed against dev —
   * `t.blocked_users.length` threw on Overview, the tenant list and Blocks &
   * suspensions, which is what the error boundary was catching.
   *
   * Read the count through `blockedUserCount`, never these fields directly.
   */
  blocked_users?: string[];
  /** Replaces `blocked_users` on this response. See above. */
  blocked_users_count?: number;
}

/**
 * How many users are blocked at this merchant, whichever shape the API sent.
 *
 * The count when it is there, the array's length while it still is, and 0 when
 * neither — because "the field is gone" is not "nobody is blocked", and the
 * only honest floor is the one that claims nothing.
 */
export function blockedUserCount(t: {
  blocked_users?: string[];
  blocked_users_count?: number;
}): number {
  return t.blocked_users_count ?? t.blocked_users?.length ?? 0;
}

export interface McpTenantList {
  /**
   * Optional, not because a real list ever omits it, but because
   * `client.ts`'s `as T` cast means nothing here is actually guaranteed — a
   * malformed or truncated response arrives typed as if it were valid. Read
   * through `tenantsOf`, never this field directly.
   */
  tenants?: McpTenantSummary[];
  count?: number;
  known_tiers?: string[];
  /**
   * Emails with a ("*", email) row — blocked EVERYWHERE.
   *
   * Reported once, separately, because it is not a property of any tenant in
   * the list. Attaching it per-merchant would misreport scope.
   */
  users_blocked_across_all_merchants?: string[];
  note?: string;
}

/** `data.tenants`, defaulting to empty rather than throwing on `.filter`/`.map`. */
export function tenantsOf(l: McpTenantList | undefined | null): McpTenantSummary[] {
  return l?.tenants ?? [];
}

/** Which blocklist rows matched. Up to three, each a different scope. */
export interface BlocklistMatch {
  merchant_wide?: BlocklistRow;
  user_everywhere?: BlocklistRow;
  user_in_merchant?: BlocklistRow;
}

export interface BlocklistRow {
  business_reference?: string;
  email?: string;
  reason?: string;
  added_by?: string;
  added_at?: string;
}

export interface McpTenantDetail {
  business_reference: string;
  business_name: string;
  email: string;
  /**
   * What is actually STORED — `null` when no CURRENT entitlements row exists.
   * A GET never creates one, so null is the honest answer rather than "free".
   */
  tier: string | null;
  /** Who last set it, e.g. "admin:someone@foodics.com". */
  tier_source?: string;
  tier_updated_at?: string;
  /** What the server actually applies: `tier ?? "free"`. */
  effective_tier: string;
  tier_is_implicit: boolean;
  /**
   * MERCHANT-WIDE block only — the ("<ref>", "*") row. Flat, replacing the old
   * `blocklist: {blocked, matched}` object: a merchant lookup only ever has
   * the merchant-wide scope to report, so `matched`'s three-way split (which
   * belongs to the per-USER route) never applied here and the nesting bought
   * nothing.
   */
  blocked: boolean;
  /** Present only when `blocked` is true. */
  blocked_since?: string;
  blocked_by?: string;
  blocked_reason?: string;
  /** Emails blocked at this merchant specifically — the per-user rows. */
  blocked_users?: string[];
}

/**
 * `caps` and `unprotected_resolvers` were REMOVED from this response — caps
 * live on `GET /admin/quota/{tier}` (already linked from the merchant page),
 * and the resolver guard they warned about no longer exists server-side (see
 * `RESOLVER_GUARD_REMOVED` below). Nothing should read either field from
 * `McpTenantDetail`; this alias exists only so a stray access is a compile
 * error naming the reason, not a silent `any`.
 */

export interface McpTierHistory {
  business_reference: string;
  history?: {
    tier: string;
    /**
     * "signup" (auto, first use) or "admin:{actor}". Never a bare "admin".
     * Nullable for the same reason `AuditEvent.actor` is: a row without one
     * fed a bare `.replace()` inside a `.map`, which is Incident 1's exact
     * shape one file over.
     */
    source: string | null;
    created_at: string | null;
  }[];
}

/** `data.history`, defaulting to `[]`. */
export function tierHistoryOf(h: McpTierHistory | undefined | null): NonNullable<McpTierHistory["history"]> {
  return h?.history ?? [];
}

// ------------------------------------------------------------------ usage --

export interface McpToolUsage {
  spent: number;
  cap: number;
  /** "unlimited" | "gated" | "metered (N/day)" — the API's own vocabulary. */
  mode: string;
  cap_source: string;
  /** True ONLY when cap > 0 and spent >= cap. See `usageStatus`. */
  exhausted: boolean;
}

export interface McpUsage {
  business_reference: string;
  tier: string;
  /** UTC calendar day. State the boundary in the UI — 02:00 in Riyadh is yesterday. */
  day: string;
  day_boundary: string;
  tools?: Record<string, McpToolUsage>;
  total_credits: number;
  note?: string;
}

/** `data.tools`, defaulting to `{}`. */
export function toolUsageOf(u: McpUsage | undefined | null): Record<string, McpToolUsage> {
  return u?.tools ?? {};
}

// ------------------------------------------------------------------ users --

export interface McpUserBlockState {
  business_reference: string;
  // The server calls this `user_id`, not `email` — it is the normalised email
  // value, just under that key (admin/app.py:308-311).
  user_id: string;
  blocked: boolean;
  matched?: BlocklistMatch;
}

// ---------------------------------------------------------------- catalog --

/**
 * `resolver_tools` and `resolver_report_dependents` are GONE — MCP removed the
 * resolver-cap guard entirely (its own Postman collection now warns "NO 409
 * WILL STOP YOU"). There is no live data to build a resolver warning from;
 * see `RESOLVER_GUARD_REMOVED` for where the warning lives instead.
 */
export interface McpCatalog {
  tools?: string[];
  note?: string;
}

/** `data.tools`, defaulting to `[]`. */
export function toolsOf(c: McpCatalog | undefined | null): string[] {
  return c?.tools ?? [];
}

/**
 * MCP dropped the resolver-cap guard (commit `0bf50a1`) — capping a
 * `search_*` tool now succeeds unconditionally. This is the one place that
 * fact is said aloud; every call site that used to render a resolver warning
 * points here instead of reconstructing its own copy.
 */
export const RESOLVER_GUARD_REMOVED =
  "MCP no longer refuses this. Capping a search_* tool can strand every " +
  "report that resolves IDs through it — the guard that used to catch that " +
  "before the write landed has been removed server-side, on dev and prod " +
  "alike. Nothing here will stop you either.";

export interface McpTiers {
  /** Optional for the usual reason — `client.ts` validates nothing. Read it
   *  through `tiersOf`, never directly: spreading an absent array throws
   *  "known is not iterable" and takes the page down. */
  tiers?: string[];
  note?: string;
}

/** `data.tiers`, defaulting to `[]`. */
export function tiersOf(t: McpTiers | undefined | null): string[] {
  return t?.tiers ?? [];
}

// ------------------------------------------------------------------ quota --

export interface McpQuotaAll {
  /**
   * tier → tool → cap. An empty map for a tier means unlimited by design.
   * Optional per the `client.ts` cast note on `McpTenantList.tenants` — read
   * through `capsOf`, never this field directly.
   */
  caps?: Record<string, Record<string, number>>;
  modes?: Record<string, string>;
  /** ~300. The cap cache TTL — never say "instant" in the UI. */
  effective_within_seconds?: number;
  note?: string;
}

/** One lane's rate, as both the stored document and `effective` carry it. */
export interface McpRateLimitLaneValue {
  max_requests?: number;
  per_seconds?: number;
  /** `effective` only: `tier` or `code default`. Absent on the stored document. */
  source?: string;
}

/**
 * One tier's quota document.
 *
 * `ratelimit` LIVES HERE NOW. It used to be four flat SSM keys written by
 * `PATCH /admin/ratelimit/{lane}`, which MCP retired — rate limits are part
 * of the tier, set with the caps, and `effective` fills in the code defaults
 * so a panel can say whether a lane is the tier's own or inherited.
 *
 * Note `per_seconds`, NOT `window_seconds`: the old lane route's spelling.
 */
export interface McpQuotaTier {
  tier: string;
  caps?: Record<string, number>;
  /** `document`, `legacy rows` or `none`. */
  source?: string;
  /** Null when there is no document. Writes are optimistic on it. */
  version?: number | null;
  config?: {
    tool_default?: number | null;
    max_calls_per_day?: number | null;
    tools?: Record<string, number | null>;
    ratelimit?: Partial<Record<RateLimitLane, McpRateLimitLaneValue | null>>;
  };
  effective?: {
    max_calls_per_day?: { value?: number; mode?: string };
    tool_default?: { value?: number; mode?: string };
    ratelimit?: Partial<Record<RateLimitLane, McpRateLimitLaneValue>>;
  };
  effective_within_seconds?: number;
  legacy_rows_ignored?: unknown;
}

/**
 * One lane's effective rate for a tier, or null when it cannot be read.
 *
 * Reads `effective`, not `config`: a tier with no stored lane still HAS a
 * rate — the server's code default — and rendering the stored document alone
 * would show an empty field where a real limit applies.
 */
export function tierRateLimitOf(
  q: McpQuotaTier | undefined | null,
  lane: RateLimitLane,
): McpRateLimitLaneValue | null {
  return q?.effective?.ratelimit?.[lane] ?? null;
}

/** `data.caps`, defaulting to `{}` rather than throwing on `Object.entries`. */
export function capsOf<T extends string>(
  q: { caps?: Record<T, number> } | undefined | null,
): Record<T, number> {
  return q?.caps ?? ({} as Record<T, number>);
}

/**
 * `McpQuotaAll.caps` — tier → tool → cap — is a DIFFERENT shape from
 * `McpQuotaTier.caps` (one tier's own tool → cap map), so it needs its own
 * total accessor rather than overloading `capsOf` onto both.
 */
export function allCapsOf(
  q: { caps?: Record<string, Record<string, number>> } | undefined | null,
): Record<string, Record<string, number>> {
  return q?.caps ?? {};
}

// --------------------------------------------------------------- settings --

export interface McpSettingParam {
  key: string;
  value: string | null;
  /** False means never seeded — the code default applies. Not an error. */
  exists: boolean;
  writable: boolean;
  /** When a change reaches running tasks. Differs per key; show it verbatim. */
  effect: string;
}

export interface McpSettings {
  parameters?: McpSettingParam[];
  writable?: string[];
  note?: string;
}

/**
 * `data.writable`, defaulting to `[]`.
 *
 * `new Set(s.writable)` on an absent field is legal JS and silently produces
 * an EMPTY set — every writable key would then render as read-only, with no
 * error anywhere. This is the guarded version; nothing should call
 * `new Set(s.writable)` directly.
 */
export function writableKeysOf(s: McpSettings | undefined | null): string[] {
  return s?.writable ?? [];
}

/** `data.parameters`, defaulting to `[]`. */
export function settingsParamsOf(s: McpSettings | undefined | null): McpSettingParam[] {
  return s?.parameters ?? [];
}

// ------------------------------------------------------- rate limits -------

/**
 * The two lanes `PATCH /admin/ratelimit/{lane}` accepts. Anything else 422s
 * naming this exact set.
 */
export const RATE_LIMIT_LANES = ["core", "reports"] as const;
export type RateLimitLane = (typeof RATE_LIMIT_LANES)[number];

/**
 * The SSM key each lane's ceiling lives under — read-only everywhere,
 * including here. `GET /admin/settings` returns both as ordinary read-only
 * `parameters` rows; there is no dedicated ceiling route.
 */
export const RATE_LIMIT_CEILING_KEY: Record<RateLimitLane, string> = {
  core: "ratelimit/foodics_core_ceiling_per_min",
  reports: "ratelimit/foodics_reports_ceiling_per_hour",
};

/** The window the ceiling itself is expressed over — 60s for core, 1h for reports. */
export const RATE_LIMIT_CEILING_WINDOW_SECONDS: Record<RateLimitLane, number> = {
  core: 60,
  reports: 3600,
};

/**
 * The four now-read-only keys `PATCH /admin/settings` refuses to write.
 * Still present in `GET /admin/settings`'s `parameters` list (`writable:
 * false`) — kept here so a settings page can single them out for "moved,
 * see the rate-limit editor" copy rather than rendering them as an ordinary
 * dead read-only row with no explanation.
 */
export const RATE_LIMIT_MOVED_KEYS = [
  "ratelimit/max_requests",
  "ratelimit/window_seconds",
  "ratelimit/reports_max_requests",
  "ratelimit/reports_window_seconds",
] as const;

/** A lane's live ceiling, read off the same settings parameters list. `null` if unseeded. */
export function rateLimitCeilingOf(params: McpSettingParam[], lane: RateLimitLane): number | null {
  const value = params.find((p) => p.key === RATE_LIMIT_CEILING_KEY[lane])?.value ?? null;
  return value !== null ? Number(value) : null;
}

/**
 * The same normalization the server applies, so a client-side preview can
 * show "normalizes to X per Y" before submit without pretending to be the
 * real gate — the 422 is still the only actual enforcement.
 */
export function normalizedRate(
  maxRequests: number,
  windowSeconds: number,
  lane: RateLimitLane,
): number {
  return (maxRequests / windowSeconds) * RATE_LIMIT_CEILING_WINDOW_SECONDS[lane];
}

/** `PATCH /admin/ratelimit/{lane}` — one applied key's before/after. */
export interface RateLimitAppliedValue {
  value: string;
  before: string;
}

/** Response body of a successful `PATCH /admin/ratelimit/{lane}`. */
export interface RateLimitApplyResponse {
  lane: RateLimitLane;
  applied: Record<string, RateLimitAppliedValue>;
  normalized_rate: number;
  ceiling: number;
  ceiling_window_seconds: number;
  effect: string;
  note?: string;
}

/**
 * The extra fields a 422 from this route carries, beyond `code`/`message` —
 * read off `ApiError.body`, which is `unknown`-valued by construction. Guard
 * every field; a 422 from an unrelated route (or a future server change)
 * will not have any of them.
 */
export interface RateLimitRefusalBody {
  lane?: RateLimitLane;
  ceiling?: number;
  ceiling_window_seconds?: number;
  normalized_rate?: number;
}

// ------------------------------------------------------- derived helpers ---

/**
 * A cap's tone. An alias, not a second definition — see components/tone.ts.
 * The domain name is kept because `capLabel` returns a cap's tone, and a
 * reader of that signature should not have to know it is the shared union.
 */
export type CapTone = Tone;

/** -1 unlimited · 0 gated · N metered. Rendering the raw number misleads. */
export function capLabel(n: number): { text: string; tone: CapTone } {
  if (n < 0) return { text: "Unlimited", tone: "green" };
  if (n === 0) return { text: "Gated", tone: "red" };
  return { text: `${n}/day`, tone: "neutral" };
}

export type UsageStatus =
  | { kind: "gated"; text: string; tone: CapTone }
  | { kind: "exhausted"; text: string; tone: CapTone }
  | { kind: "metered"; text: string; tone: CapTone }
  | { kind: "unlimited"; text: string; tone: CapTone };

/**
 * One status per tool, computed — because `exhausted` lies.
 *
 * The API sets `exhausted` true only when `cap > 0 && spent >= cap`, so a
 * GATED tool (cap 0, never allowed) reports `exhausted: false` despite being
 * strictly more restricted than an exhausted one. Binding that field directly
 * to an "Exhausted" column tells the operator the opposite of the truth, so
 * there is no such column — this function is the only reader.
 */
export function usageStatus(u: McpToolUsage): UsageStatus {
  if (u.cap === 0) {
    return { kind: "gated", text: "Gated — never allowed", tone: "red" };
  }
  if (u.cap < 0) {
    return { kind: "unlimited", text: `Unlimited · ${u.spent} used`, tone: "green" };
  }
  if (u.exhausted || u.spent >= u.cap) {
    return { kind: "exhausted", text: `Exhausted · ${u.spent} of ${u.cap}`, tone: "amber" };
  }
  return { kind: "metered", text: `${u.spent} of ${u.cap}`, tone: "neutral" };
}

export interface CapChange {
  tool: string;
  from: number | undefined;
  to: number | undefined;
}

/**
 * What changes for a merchant moving between tiers.
 *
 * Turns an opaque label swap into a statement of what they will experience.
 * A tool absent from a tier's map inherits that tier's "*" default, so the
 * comparison resolves through it rather than treating absence as "no change".
 */
export function capDiff(
  from: Record<string, number>,
  to: Record<string, number>,
): CapChange[] {
  const tools = new Set([...Object.keys(from), ...Object.keys(to)]);
  tools.delete("*");

  const fromDefault = from["*"];
  const toDefault = to["*"];
  const changes: CapChange[] = [];

  for (const tool of [...tools].sort()) {
    const a = from[tool] ?? fromDefault;
    const b = to[tool] ?? toDefault;
    if (a !== b) changes.push({ tool, from: a, to: b });
  }

  if (fromDefault !== toDefault) {
    changes.unshift({ tool: "*", from: fromDefault, to: toDefault });
  }
  return changes;
}

/**
 * The sentinel for "no entitlements row", used by tier filters in the UI.
 *
 * DELIBERATELY NOT the string "none", and this is the subtlest trap in the
 * whole API:
 *
 *   - `GET /admin/tenants?tier=` is an EXACT MATCH on the stored value. A
 *     tier-less merchant stores `null`, and no merchant ever stores the string
 *     "none" — so `?tier=none` returns an empty list, silently, and reads as
 *     "we have no such merchants" rather than "that filter cannot express it".
 *   - `GET /admin/fleet/metrics`' `tier_distribution` DOES use `"none"` as a
 *     key, for the unrelated reason that a JSON object needs a string key and
 *     `null` cannot be one.
 *
 * The two look inconsistent side by side and neither is wrong. The danger is
 * wiring a distribution bucket straight into a list filter — the "none" bucket
 * would show a count and then drill through to nothing.
 *
 * So: this sentinel never goes on the wire. `matchesTier` resolves it
 * client-side, and `tierQuery` returns undefined for it rather than sending a
 * value the server would match against literally.
 */
export const TIER_UNSET = "\u0000unset";

/** The `tier_distribution` key for merchants with no entitlements row. */
export const TIER_DISTRIBUTION_UNSET_KEY = "none";

/** Does this merchant match a tier filter? `TIER_UNSET` means "no row stored". */
export function matchesTier(t: { tier: string | null }, filter: string): boolean {
  if (!filter) return true;
  if (filter === TIER_UNSET) return t.tier === null;
  return t.tier === filter;
}

/**
 * The `?tier=` value to send, or undefined when the filter cannot be expressed
 * server-side. Callers must filter client-side when this returns undefined for
 * a non-empty filter — see TIER_UNSET.
 */
export function tierQuery(filter: string): string | undefined {
  if (!filter || filter === TIER_UNSET) return undefined;
  return filter;
}

/** `GET /mcp/admin/tenants`, with `?tier=` only when the server can express it. */
export function tenantsListPath(tierFilter: string): string {
  const t = tierQuery(tierFilter);
  if (!t) return "/mcp/admin/tenants";
  return `/mcp/admin/tenants?tier=${encodeURIComponent(t)}`;
}

// ---------------------------------------------------------------- audit ----

/** The domains the audit table keys on. Anything else is a 422. `mcp_clients` is the
 *  AI-app allowlist (add/remove an app; `before`/`after` are the URI LIST). */
export const AUDIT_DOMAINS = ["tenant", "quota", "settings", "mcp_clients"] as const;
export type AuditDomain = (typeof AUDIT_DOMAINS)[number];

/**
 * Fleet or per-domain audit list. Cursor is opaque — pass it via the client's
 * `query` option so a second `?` cannot wipe `days`/`limit`. Domain lists are
 * not day-windowed.
 */
export function auditListPath(domain: AuditDomain | ""): string {
  const base = domain ? `/mcp/admin/audit/${domain}` : "/mcp/admin/audit";
  const q = new URLSearchParams();
  if (!domain) q.set("days", "7");
  q.set("limit", "50");
  return `${base}?${q.toString()}`;
}

/**
 * `DELETE /admin/quota/{tier}/{tool}` — removes one override.
 *
 * `now_resolves_to` is the field that matters and the one easy to skip: it is
 * what the tool ACTUALLY falls back to, and for the "*" row it is not the
 * tier's own default but the FREE tier's map, via the unknown-tier path.
 * Computing a success message from the tier's caps instead of from this would
 * tell an operator the wrong number in exactly the case that matters most.
 */
export interface McpQuotaDelete {
  tier: string;
  tool: string;
  deleted: boolean;
  previous: number;
  now_resolves_to: number;
  /** ~300. The cap cache TTL — never say "instant". */
  effective_within_seconds: number;
}

/**
 * One admin action. NOT a merchant tool call — a different table answering a
 * different question, and the API reference is explicit that the two must
 * never share a panel.
 */
export interface AuditEvent {
  domain: AuditDomain;
  /** e.g. "set_tier", "block", "set_cap". */
  action: string;
  /**
   * `admin:{email}` — from the verified JWT since dual-auth shipped.
   *
   * NULLABLE, like `target` and `timestamp` below, and none of the three is a
   * hypothetical. The reference documents all three as always present; dev's
   * table holds at least one row without a `timestamp`, and reading it as a
   * string threw inside the row map — which in React 19 unmounts the whole
   * root, so ONE legacy row blanked the entire console, shell included.
   *
   * They are typed as nullable so a bare `.replace`/`.split`/`.startsWith` on
   * any of them is a compile error rather than a white screen. An audit log is
   * the last surface that should fall over: it is what you open when something
   * has already gone wrong.
   */
  actor: string | null;
  /**
   * What changed: a merchant reference, a tier, an SSM key. May hold a slash.
   *
   * `unknown`, not `string | null`, for the same reason `before`/`after` are.
   * `admin/services/audit.py`'s `_deserialize` reads `attr.get("S")` and, for
   * any attribute that is NOT a DynamoDB string, assigns the raw attribute
   * WRAPPER instead — `{"N": "5"}`, `{"BOOL": true}` — for every key on the
   * row, this one included. Today's writer only ever writes strings, but a
   * legacy row is precisely what already happened with `timestamp`, and this
   * field feeds `encodeURIComponent` and `.split("/")` as well as JSX. Read
   * it through `auditTarget()`.
   */
  target: unknown;
  /**
   * Whatever JSON value the action wrote — NOT always a string.
   *
   * The server stores every value as a DynamoDB string, JSON-encoding
   * anything that is not one already, then decodes it back on read
   * (admin/audit_log.py, admin/services/audit.py). What comes out the other
   * side is action-shaped: a boolean for block/unblock, a string for
   * set_tier, a `{tool: cap}` dict for quota's set_caps. Rendering either
   * field directly as a JSX child is exactly React error #31 the moment a
   * quota row shows up — use `auditValue()` below, never `{e.before}`.
   */
  before: unknown;
  after: unknown;
  timestamp: string | null;
  /**
   * Why the operator said they made this change.
   *
   * THE CONSOLE COLLECTED THIS AND NEVER SHOWED IT. Every L2 write takes a
   * reason through `ConfirmDialog` (min 10 chars), sends it, and MCP stores
   * it on the row — but this interface did not declare the field, so the
   * audit log rendered actor/action/target and silently dropped the one
   * part a reader six months later actually needs.
   *
   * Optional because it is only there when the write sent one: older rows,
   * and the few writes that take no reason, have none.
   */
  reason?: string | null;
  request_id?: string;
}

/**
 * `before`/`after` as one line of text, whatever JSON type they decoded to.
 *
 * A string renders as itself; anything else (a bool, a dict, a number) is
 * stringified rather than handed to JSX, which is the only thing standing
 * between a quota row's `{tool: cap}` object and React error #31.
 */
/**
 * An audit row's `target` as a string, or null when it is not usable as one.
 *
 * Null rather than a marker string, because callers do two different things
 * with it: build a URL (`encodeURIComponent`, `.split("/")`) and render it.
 * A caller that must not construct `/tenants/[object%20Object]` needs to know
 * the value was unusable, which a "not recorded" string would hide.
 */
export function auditTarget(v: unknown): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

export function auditValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "boolean" || typeof v === "number") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export interface AuditPage {
  events?: AuditEvent[];
  /**
   * Opaque. Pass it back verbatim as `?cursor=`; never construct or parse one
   * — it encodes a DynamoDB pagination key. Null when there is no more.
   */
  cursor?: string | null;
  count?: number;
}

/**
 * `data.events`, defaulting to `[]`.
 *
 * `useAuditFeed`'s page-append does `[...prev.data.events, ...page.events]`
 * inside a `setState` updater — a throw there is worse than one during render,
 * because it fires outside the commit path with a stack trace that does not
 * name the component. Always read through this, including inside that spread.
 */
export function eventsOf(p: AuditPage | undefined | null): AuditEvent[] {
  return p?.events ?? [];
}

/** `admin:someone@foodics.com` → `someone@foodics.com`. Total, by design. */
export function actorEmail(actor: string | null | undefined): string {
  if (!actor) return "unknown actor";
  return actor.startsWith("admin:") ? actor.slice("admin:".length) : actor;
}

/**
 * An audit row's UTC stamp, rendered.
 *
 * `dateOnly` gives `2026-09-02`; otherwise `2026-09-02 08:41:00 UTC`. Returns
 * a marker rather than throwing when the row carries no stamp — see the note
 * on `AuditEvent.actor` for why that is not theoretical.
 */
export function auditStamp(
  timestamp: string | null | undefined,
  opts: { dateOnly?: boolean } = {},
): string {
  if (!timestamp) return "no timestamp";
  return opts.dateOnly
    ? timestamp.slice(0, 10)
    : `${timestamp.replace("T", " ").slice(0, 19)} UTC`;
}

// -------------------------------------------------------- fleet metrics ----
//
// `GET /admin/fleet/metrics` (one bundled response, one `generated_at` for
// all four fields at once) is GONE. MCP replaced it with `GET
// /admin/metrics/{metric}` — one metric per call, envelope `{metric, value}`,
// no shared timestamp because there is no longer one read that produces all
// of them together. Seven metrics exist today; the original four plus three
// nothing in the console reads yet.
//
// Each call is independent, so each gets its own `useResource` and its own
// loading/error state — one metric failing must not blank the other six.

export const FLEET_METRICS = [
  "usage_today",
  "tier_distribution",
  "blocked",
  "admin_actions_today",
  "tenants_count",
  "top_tools_today",
  "quota_overrides_count",
] as const;
export type FleetMetricKey = (typeof FLEET_METRICS)[number];

/** The `value` shape per metric — matches `admin/services/fleet.py::metric()` exactly. */
export interface FleetMetricValue {
  usage_today: { day: string; total_credits: number };
  /**
   * Tenant count per tier. Merchants with no entitlements row are bucketed
   * under the literal key "none" — see TIER_DISTRIBUTION_UNSET_KEY, and do NOT
   * pass that key to `?tier=`.
   */
  tier_distribution: Record<string, number>;
  blocked: { merchants: number; users_everywhere: number };
  /**
   * Admin actions recorded today. A UTC CALENDAR DAY, not a rolling 24 hours —
   * it resets at UTC midnight, the same boundary as usage_today. Label it as
   * such: an operator in Riyadh reading "last 24 hours" at 02:00 would be
   * looking at a number that just reset.
   */
  admin_actions_today: number;
  /** Total merchants ever connected (a PROFILE row exists). */
  tenants_count: number;
  /** Top 10 tools by fleet-wide credit spend today, descending. */
  top_tools_today: { tool: string; credits: number }[];
  /** Explicit cap overrides per tier — the quotas table is empty by default. */
  quota_overrides_count: Record<string, number>;
}

export interface FleetMetricEnvelope<K extends FleetMetricKey = FleetMetricKey> {
  metric: K;
  /**
   * Optional for the reason every other field on every other response here
   * is: `client.ts` casts with `as T` and validates nothing, so a truncated
   * or unexpected 200 body arrives typed as a valid envelope with `value`
   * actually undefined. Six tiles then did `.value.toLocaleString()` or
   * `Object.entries(.value)` and took the whole page down.
   *
   * Read it through `metricValue`, never directly.
   */
  value?: FleetMetricValue[K];
}

/** `GET /mcp/admin/metrics/{key}` — one metric, one request. */
export function metricPath(key: FleetMetricKey): string {
  return `/mcp/admin/metrics/${key}`;
}

/**
 * A metric's value from its resource state, or null when there isn't one.
 *
 * ONE accessor for all seven metrics, because the failure is identical in
 * every case and a per-tile guard is a per-tile thing to forget: a metric that
 * is still loading, that failed, or that came back without a `value` all mean
 * the same thing to a tile — there is no number to draw yet.
 *
 * Takes the Resource rather than the envelope so the `kind === "ready"` check
 * cannot be skipped at a call site either.
 */
export function metricValue<K extends FleetMetricKey>(
  state: { kind: string; data?: FleetMetricEnvelope<K> },
): FleetMetricValue[K] | null {
  if (state.kind !== "ready") return null;
  return state.data?.value ?? null;
}


// ------------------------------------------------------------- MCP clients --
//
// Which AI apps may connect to MCP: the allowlist of OAuth redirect URIs.
// Source of truth: MCP admin/services/mcp_clients.py (read() / add() / remove() /
// requested()). Every field optional, for the reason at the top of CLAUDE.md:
// client.ts validates nothing, so these are assertions; read them through the
// helpers below.

/** `GET /admin/mcp-clients`. */
export interface McpClients {
  /** Fixed: Claude, ChatGPT connectors, local desktop apps. Not editable here. */
  baseline?: string[];
  /** Added from the console; removable. */
  extra?: string[];
  /** baseline ∪ extra: what MCP actually checks. */
  effective?: string[];
  max_extra?: number;
  live_within_seconds?: number;
  note?: string;
}

/** One app MCP refused for its redirect URI (`GET /admin/mcp-clients/requested`). */
export interface McpRequestedApp {
  redirect_uri?: string;
  /** What the app said about itself; null when it said nothing. */
  client_name?: string | null;
  client_id?: string | null;
  tries?: number;
  first_seen?: string | null;
  last_seen?: string | null;
  /** "register" or "authorize": where MCP refused it. */
  via?: string | null;
  /** false = an unfamiliar domain. Check it before allowing (phishing risk). */
  known_host?: boolean;
  /** false = POST would refuse this address anyway; `problem` says why. */
  can_allow?: boolean;
  problem?: string | null;
}

export interface McpRequestedApps {
  requested?: McpRequestedApp[];
  total?: number;
  /** Requests whose address has been allowed since (no longer listed). */
  now_allowed?: number;
  as_of?: string;
  note?: string;
}

/** `POST` / `DELETE /admin/mcp-clients` response. */
export interface McpClientWriteResponse {
  uri?: string;
  before?: string[];
  after?: string[];
  known_host?: boolean;
  live_within_seconds?: number;
}

export const MCP_CLIENTS_PATH = "/mcp/admin/mcp-clients";
export const MCP_CLIENTS_REQUESTED_PATH = "/mcp/admin/mcp-clients/requested";

/** The three lists, always arrays. */
export function mcpClientsOf(c: McpClients | undefined | null): {
  baseline: string[];
  extra: string[];
  effective: string[];
  maxExtra: number;
  liveWithinSeconds: number | undefined;
} {
  return {
    baseline: c?.baseline ?? [],
    extra: c?.extra ?? [],
    effective: c?.effective ?? [],
    maxExtra: c?.max_extra ?? 25,
    liveWithinSeconds: c?.live_within_seconds,
  };
}

/** The requested apps with every field defaulted, newest first as MCP sends them. */
export function requestedAppsOf(r: McpRequestedApps | undefined | null): Array<{
  redirectUri: string;
  name: string | null;
  tries: number;
  firstSeen: string | null;
  lastSeen: string | null;
  knownHost: boolean;
  canAllow: boolean;
  problem: string | null;
}> {
  return (r?.requested ?? [])
    .filter((a) => typeof a.redirect_uri === "string" && a.redirect_uri !== "")
    .map((a) => ({
      redirectUri: a.redirect_uri as string,
      name: a.client_name ?? null,
      tries: a.tries ?? 0,
      firstSeen: a.first_seen ?? null,
      lastSeen: a.last_seen ?? null,
      // Missing means "not known to be safe": the warning is the safe default.
      knownHost: a.known_host === true,
      canAllow: a.can_allow !== false,
      problem: a.problem ?? null,
    }));
}

/** The host of a redirect URI, for display ("gemini.google.com"); the URI if unparsable. */
export function redirectHost(uri: string): string {
  try {
    return new URL(uri.replace(":*", "")).hostname || uri;
  } catch {
    return uri;
  }
}
